import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  IgChip,
  IgLink,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { CustomEventFeType } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { ClickEvent } from "@src/features/customEvents/components/subpages/ClickEvent";
import { CollectionPageView } from "@src/features/customEvents/components/subpages/CollectionPageView";
import { ElementViewed } from "@src/features/customEvents/components/subpages/ElementViewed";
import { JsEvent } from "@src/features/customEvents/components/subpages/JsEvent";
import { JsImportedEvent } from "@src/features/customEvents/components/subpages/JsImportedEvent";
import { PageView } from "@src/features/customEvents/components/subpages/PageView";
import { ProductPageView } from "@src/features/customEvents/components/subpages/ProductPageView";
import { ScrollDepth } from "@src/features/customEvents/components/subpages/ScrollDepth";
import { eventTypes } from "@src/features/customEvents/helpers";
import { RenderCustomEventIcon } from "@src/features/customEvents/utils";
import { MoreMenuArchiveSubpage } from "@src/features/eventsManager/components/MoreMenuArchivedSubpage";
import {
  selectedCurrentCustomEvent,
  setcurrentEventDescription,
  setcurrentEventName,
} from "@src/store/slices/customEvents";
import { openNewTab } from "@src/utils/openNewTab";

interface CustomEventSubpageWrapperProps {
  eventType: CustomEventFeType;
}

export const CustomEventSubpageWrapper = ({
  eventType,
}: CustomEventSubpageWrapperProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const currentEvent = useSelector(selectedCurrentCustomEvent);

  const archiveChipTooltipTitle = (
    <IgTypography>
      This event is archived. It won’t be available for tracking when setting up
      a test, but may still be actively tracked in existing tests. Go to the{" "}
      <strong>Events Manager</strong> and <strong>Unarchive</strong> it to make
      it available for tracking in new tests.
    </IgTypography>
  );

  const SubpageContent = useMemo(() => {
    switch (eventType) {
      case "productPageView":
        return <ProductPageView isArchived={!!currentEvent?.archivedAtTs} />;
      case "collectionPageView":
        return <CollectionPageView isArchived={!!currentEvent?.archivedAtTs} />;
      case "pageView":
        return <PageView isArchived={!!currentEvent?.archivedAtTs} />;
      case "clickEvent":
        return <ClickEvent isArchived={!!currentEvent?.archivedAtTs} />;
      case "elementViewed":
        return <ElementViewed isArchived={!!currentEvent?.archivedAtTs} />;
      case "scrollDepth":
        return <ScrollDepth isArchived={!!currentEvent?.archivedAtTs} />;
      case "javascriptEvent":
        return <JsEvent isArchived={!!currentEvent?.archivedAtTs} />;
      case "javascriptImportedEvent":
        return <JsImportedEvent />;
      default:
        return null;
    }
  }, [eventType]);

  return (
    <Grid container width={"100%"}>
      <Grid
        container
        direction={"row"}
        justifyContent={"flex-start"}
        alignItems={"center"}
        marginTop={"15px"}
        wrap={"nowrap"}
      >
        <Grid container width={"fit-content"}>
          <RenderCustomEventIcon eventType={eventType} />
        </Grid>
        <Grid container direction={"column"} marginLeft={"20px"}>
          <Grid item height={"25px"}>
            <IgTypography fontWeight={600} variant={"h2"}>
              {!currentEvent?.name || currentEvent?.name === ""
                ? (eventTypes.find((event) => event.key === eventType)?.title ??
                  "")
                : currentEvent.name}
            </IgTypography>
          </Grid>
          <Grid
            item
            height={"23px"}
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            <IgTypography
              color={theme.palette.text.secondary}
              sx={{
                overflow: "hidden",
                width: "520px",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {!currentEvent?.description || currentEvent?.description === ""
                ? eventTypes.find((event) => event.key === eventType)
                    ?.description
                : currentEvent.description}
            </IgTypography>
          </Grid>
        </Grid>
        {!!currentEvent?.archivedAtTs && (
          <>
            <Grid container width={"fit-content"} marginRight={"-20px"}>
              <IgTooltip title={archiveChipTooltipTitle} maxWidth={"185px"}>
                <IgChip label={"Archived"} size={"small"} />
              </IgTooltip>
            </Grid>
            <Grid>
              <MoreMenuArchiveSubpage onCopyClick={() => console.log("copy")} />
            </Grid>
          </>
        )}
      </Grid>
      {/* END OF HEADER */}

      {/* ALERTS for later */}
      {/*<Grid position={"relative"} width={"100%"} marginTop={"20px"}>*/}
      {/*  <Alert*/}
      {/*    severity={"error"}*/}
      {/*    sx={{*/}
      {/*      marginLeft: "-40px",*/}
      {/*      paddingLeft: "40px",*/}
      {/*      width: "623px",*/}
      {/*      borderRadius: "0px",*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    This is an alert for the Custom Event.*/}
      {/*  </Alert>*/}
      {/*</Grid>*/}
      {/*END OF ALERTS*/}

      {/* NAME AND DESCRIPTION INPUTS */}
      <Grid container width={"100%"} marginTop={"30px"} alignItems={"flex-end"}>
        <IgTextField
          fullWidth
          placeholder={"Name your event"}
          value={currentEvent?.name || ""}
          disabled={!!currentEvent?.archivedAtTs}
          onChange={(e) => dispatch(setcurrentEventName(e.target.value))}
        />
        <IgTextField
          fullWidth
          sx={{
            marginTop: "15px",
          }}
          placeholder={"Give your event a description (optional)"}
          value={currentEvent?.description || ""}
          disabled={!!currentEvent?.archivedAtTs}
          onChange={(e) => dispatch(setcurrentEventDescription(e.target.value))}
        />
      </Grid>
      <Grid container marginTop={"30px"}>
        <IgTypography fontWeight={600}>
          {eventTypes.find((event) => event.key === eventType)?.actionSubtitle}
          &nbsp;
        </IgTypography>
        <IgLink
          onClick={() => {
            openNewTab(
              eventTypes.find((event) => event.key === eventType)?.link || "",
              eventTypes.find((event) => event.key === eventType)?.linkTitle
            );
          }}
        >
          Learn More.
        </IgLink>
      </Grid>

      {SubpageContent}
    </Grid>
  );
};
